import {debounce, DebouncedFunction} from 'ts-debounce';
import _ from 'underscore';

import {assert} from 'Asserts';
import {EventConstants} from 'constants/EventConstants';
import * as browser from 'lib/Browser';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';
import TFIActions from 'notebook/tfi/TFIActions';
import TFIDAO from 'notebook/tfi/TFIDAO';
import notifications from 'notifications';

import {TFIAccount, TFIFileMetaState, TFIFolder, TFIObject} from './TFIConstants';

const SLOW_QUERY_CUTOFF_MS = 300;
export const CHECK_FOR_UPDATE_INTERVAL = 5000;

interface TFIData {
  results: Array<TFIObject>;
  lastQueryTime: number;
  numPendingQueries: number;
}

class TFIStore extends Store {
  _queueSearch!: DebouncedFunction<() => void>;
  accounts!: Array<TFIAccount> | null;
  listedFolderId!: string | null;
  loadedTFIObjects!: Record<string, TFIObject>;
  showTFIAddModal!: boolean;
  query!: string | null;
  tfiAccountFilter!: string | null;
  tfiData!: TFIData;
  tfiFileMetaStateById!: Record<string, TFIFileMetaState>;
  activeTFIFileMetas!: Map<number, number>;
  bulkCheckIntervalId!: NodeJS.Timeout;

  static override initClass(): void {
    this.prototype._queueSearch = debounce(function (this: TFIStore) {
      if (this.doTFIQuery()) {
        this.doSearch(this.tfiData, TFIDAO.search(this.query, 10, this.tfiAccountFilter));
      } else {
        this.tfiData.results = [];
        this.emitChange();
      }
    }, 200);
  }

  override resetState(): void {
    this.accounts = null;
    this.listedFolderId = null;
    this.loadedTFIObjects = {}; // Cache of all TFI objects that have been displayed at some point
    this.showTFIAddModal = false;
    this.resetTFISyncState();
    this.resetTFISearchState();
  }

  private resetTFISyncState(): void {
    this.tfiFileMetaStateById = {};
    this.activeTFIFileMetas = new Map();
    if (browser.isBrowser) {
      if (this.bulkCheckIntervalId != null) {
        clearInterval(this.bulkCheckIntervalId);
      }
      this.bulkCheckIntervalId = setInterval(() => {
        TFIActions.bulkCheckForUpdates(() => this.getTFIFileMetaIdsForUpdateCheck());
      }, CHECK_FOR_UPDATE_INTERVAL);
    }
  }

  private resetTFISearchState(): void {
    this.query = null;
    this.tfiData = {
      results: [],
      lastQueryTime: 0,
      numPendingQueries: 0,
    };
    this.tfiAccountFilter = null;
  }

  private addParents(tfiObject: TFIObject, parentIds: Array<string> = []): TFIObject {
    const parent =
      tfiObject.parentId != null
        ? !parentIds.includes(tfiObject.parentId)
          ? this.addParents(this.loadedTFIObjects[tfiObject.parentId], parentIds.concat([tfiObject.parentId]))
          : tfiObject // Handle the case where a shortcut to a folder is placed within the folder itself
        : null;

    return {...tfiObject, parent};
  }

  getListedFolder(): TFIFolder | null {
    let children;
    if (this.listedFolderId == null) {
      return null;
    }

    // Parent objects are only set for the listed folder and its parents (to provide a heirarchy to the root)
    const listedFolder = this.addParents(this.loadedTFIObjects[this.listedFolderId]);
    if (listedFolder.childIds != null) {
      children = listedFolder.childIds.map((id) => this.loadedTFIObjects[id]);
    } else {
      children = null; // Children have not yet loaded
    }

    return {...listedFolder, children};
  }

  getAccounts(): Array<TFIAccount> | null {
    return this.accounts;
  }

  getShowTFIAddModal(): boolean {
    return this.showTFIAddModal;
  }

  private addTFIObject(id: string, tfiObject: TFIObject): void {
    // Extend currently loaded object so that cached childIds aren't blown away
    // (if these are inaccurate they will be overwritten the next time the folder is listed)
    const loadedObject = this.loadedTFIObjects[id] ?? {};
    this.loadedTFIObjects[id] = {...loadedObject, ...tfiObject};
  }

  private addAccount(account: TFIAccount): void {
    if (this.accounts == null) {
      this.accounts = [];
    }
    this.accounts.push(account);
    this.addTFIObject(account.id, {
      id: account.id,
      name: account.name,
      accountId: account.id,
      accountType: account.accountType,
      isFolder: true,
      parentId: null,
    });
  }

  private sortAccounts(): void {
    assert(this.accounts != null, 'Expected non-null accounts');
    this.accounts = _.sortBy(this.accounts, 'name');
    this.accounts = _.sortBy(this.accounts, 'accountType');
  }

  getTFIFileMetaState(tfiMetaStateId: string): TFIFileMetaState {
    return this.tfiFileMetaStateById[tfiMetaStateId];
  }

  setTFIFileMetaState(tfiFileMetaId: string, state: TFIFileMetaState): void {
    this.tfiFileMetaStateById[tfiFileMetaId] = {
      ...this.tfiFileMetaStateById[tfiFileMetaId],
      ...state,
    };
  }

  private registerTFIFileMetaForUpdate(id: number): void {
    this.activeTFIFileMetas.set(id, (this.activeTFIFileMetas.get(id) || 0) + 1);
  }

  private deregisterTFIFileMetaForUpdate(id: number): void {
    this.activeTFIFileMetas.set(id, (this.activeTFIFileMetas.get(id) || 1) - 1);
  }

  private getTFIFileMetaIdsForUpdateCheck(): Array<number> {
    const ids: Array<number> = [];
    this.activeTFIFileMetas.forEach((count: number, tfiFileMetaId: number) => {
      if (count > 0) {
        ids.push(tfiFileMetaId);
      }
    });
    return ids;
  }

  getQuery(): string | null {
    return this.query;
  }

  getSearchActive(): boolean {
    return this.query != null;
  }

  isSearching(searchData: TFIData): boolean {
    // Only show once the query becomes a bit old - after a few hundred ms.
    const slowQuery = new Date().getTime() - searchData.lastQueryTime > SLOW_QUERY_CUTOFF_MS;
    return searchData.numPendingQueries > 0 && slowQuery;
  }

  private getSearchData(searchData: TFIData): {results: TFIData['results']; isSearching: boolean} {
    return {
      results: searchData.results,
      isSearching: this.isSearching(searchData),
    };
  }

  getTFIData(): {results: TFIData['results']; isSearching: boolean} {
    return this.getSearchData(this.tfiData);
  }

  private recordQueryTime(searchData: TFIData): void {
    // We record a @lastQueryTime, which @isSearching uses to determine if we should indicate searching.
    searchData.lastQueryTime = new Date().getTime();
    // In SLOW_QUERY_CUTOFF_MS time, emitChange so that isSearching() updates if needed.
    setTimeout(() => {
      this.emitChange();
    }, SLOW_QUERY_CUTOFF_MS);
  }

  private doTFIQuery(): boolean {
    const hasTFIAccounts = this.getAccounts() != null && this.getAccounts()!.length > 0;
    return hasTFIAccounts;
  }

  private doSearch(searchData: TFIData, searchDeferred: JQueryPromise<unknown>): void {
    this.recordQueryTime(searchData);
    const originalQuery = this.query; // Save @query to compare later
    searchData.numPendingQueries += 1;
    this.emitChange();
    Promise.resolve(searchDeferred)
      .then(({data}) => {
        searchData.numPendingQueries -= 1;
        if (this.query === originalQuery) {
          searchData.results = data;
        }
        this.emitChange();
      })
      .catch((res) => {
        searchData.numPendingQueries -= 1;
        this.emitChange();
        notifications.notifyError(res, {id: 'search-error'});
      });
  }

  override payloadHandler(action: FluxAction): boolean {
    switch (action.actionType) {
      case EventConstants.TFI_ACCOUNTS_FETCHED: {
        const accounts = action.accounts;
        this.accounts = []; // Reset accounts so we use the list from the server
        for (const account of Array.from(accounts)) {
          this.addAccount(account);
        }
        this.sortAccounts();
        this.emitChange();
        break;
      }

      case EventConstants.TFI_ACCOUNT_ADDED: {
        const {account} = action;
        this.addAccount(account);
        this.sortAccounts();
        this.emitChange();
        break;
      }

      case EventConstants.TFI_ACCOUNT_DELETED: {
        const {accountId} = action;
        assert(this.accounts != null, 'Expected non-null accounts');
        this.accounts = this.accounts.filter((account) => account.id !== accountId);

        // Remove loaded TFI objects connected to the removed account
        const newLoadedTFIObjects: Record<string, TFIObject> = {};
        for (const id of Object.keys(this.loadedTFIObjects || {})) {
          const tfiObject = this.loadedTFIObjects[id];
          if (id === accountId || tfiObject.accountId === accountId) {
            continue;
          }
          newLoadedTFIObjects[id] = tfiObject;
        }
        this.loadedTFIObjects = newLoadedTFIObjects;

        this.emitChange();
        break;
      }

      case EventConstants.TFI_FOLDER_LISTED: {
        // When a folder is opened, display any cached items
        const {folderId} = action;
        this.listedFolderId = folderId;
        this.emitChange();
        break;
      }

      case EventConstants.TFI_FOLDER_CONTENTS_FETCHED: {
        // When contents are loaded from server, display those contents
        const folderId = action.folderId;
        const tfiObjects = action.tfiObjects;
        const listedFolder = this.loadedTFIObjects[folderId];
        assert(
          listedFolder != null,
          `Contents were fetched for folder ${this.listedFolderId} that does not exist in TFIStore`
        );
        // eslint-disable-next-line no-restricted-syntax
        listedFolder.childIds = _.pluck(tfiObjects, 'id');

        for (const tfiObject of Array.from(tfiObjects)) {
          this.addTFIObject(tfiObject.id, {...tfiObject, parentId: folderId});
        }

        this.emitChange();
        break;
      }

      case EventConstants.TFI_BULK_REGISTER_REMOTE_REVISION_ID:
        for (const [tfiFileMetaId, thirdPartyFileRev] of Object.entries(
          action.thirdpartyFileRevsByTFIMetaId
        )) {
          this.setTFIFileMetaState(tfiFileMetaId, {
            upstreamThirdpartyFileRev: thirdPartyFileRev,
          });
        }
        this.emitChange();
        break;

      case EventConstants.TFI_FILE_META_CHECK_FOR_UPDATES:
        this.setTFIFileMetaState(String(action.tfiFileMetaId), {
          checkingForUpdates: true,
        });
        this.emitChange();
        break;

      case EventConstants.TFI_FILE_META_CHECK_FOR_UPDATES_COMPLETED:
        this.setTFIFileMetaState(String(action.tfiFileMetaId), {
          checkingForUpdates: false,
        });
        this.emitChange();
        break;

      case EventConstants.TFI_FILE_META_REGISTER_CHECK_FOR_UPDATES: {
        const {tfiFileMetaId} = action;
        this.registerTFIFileMetaForUpdate(tfiFileMetaId);
        this.emitChange();
        break;
      }

      case EventConstants.TFI_FILE_META_DEREGISTER_CHECK_FOR_UPDATES: {
        const {tfiFileMetaId} = action;
        this.deregisterTFIFileMetaForUpdate(tfiFileMetaId);
        this.emitChange();
        break;
      }

      case EventConstants.TFI_LIST_ACCOUNTS: {
        this.listedFolderId = null;
        this.emitChange();
        break;
      }

      case EventConstants.TFI_TOGGLE_ADD_MODAL:
        this.showTFIAddModal = action.show;
        this.emitChange();
        break;

      case EventConstants.TFI_SEARCH_SET_QUERY:
        this.query = action.query;
        this._queueSearch();
        this.emitChange();
        break;

      case EventConstants.TFI_SEARCH_SET_TFI_ACCOUNT_FILTER:
        this.tfiAccountFilter = action.tfiAccountFilter;
        this._queueSearch();
        this.emitChange();
        break;

      case EventConstants.TFI_SEARCH_CLEAR_SEARCH:
        this.query = null;
        this.emitChange();
        break;

      default:
        break;
    }

    return true;
  }
}

TFIStore.initClass();

export default new TFIStore(AppDispatcher);
